/**
 * NetworkErrorModal - Modal with automatic retry for network failures
 * 
 * Shows when network requests fail and provides:
 * - Manual "Retry" button
 * - Auto-retry with exponential backoff
 * - Visual feedback during retry attempts
 * - Automatic dismissal on success
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { WifiOff, RefreshCw, CheckCircle, Loader2 } from 'lucide-react';

const NetworkErrorModal = ({
  isOpen,
  onClose,
  onRetry,          // Function to retry the failed operation
  title = 'Network Error',
  message = 'Unable to connect to the server. Please check your connection.',
  autoRetry = true, // Whether to auto-retry
  maxAutoRetries = 5,
  baseDelay = 2000, // 2 seconds
}) => {
  const [retryState, setRetryState] = useState('idle'); // idle | retrying | success | failed
  const [retryCount, setRetryCount] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const retryTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, []);

  // Calculate delay with exponential backoff
  const getRetryDelay = useCallback((attempt) => {
    const delay = baseDelay * Math.pow(1.5, attempt);
    return Math.min(delay, 30000); // Max 30 seconds
  }, [baseDelay]);

  // Execute retry
  const executeRetry = useCallback(async () => {
    if (!mountedRef.current || !onRetry) return;
    
    setRetryState('retrying');
    
    try {
      await onRetry();
      
      if (mountedRef.current) {
        setRetryState('success');
        // Auto-close after success
        setTimeout(() => {
          if (mountedRef.current) {
            onClose();
          }
        }, 1000);
      }
    } catch (error) {
      if (mountedRef.current) {
        setRetryCount(prev => prev + 1);
        setRetryState('failed');
      }
    }
  }, [onRetry, onClose]);

  // Schedule auto-retry
  const scheduleAutoRetry = useCallback(() => {
    if (!autoRetry || retryCount >= maxAutoRetries) return;
    
    const delay = getRetryDelay(retryCount);
    const delaySeconds = Math.ceil(delay / 1000);
    
    setCountdown(delaySeconds);
    
    // Countdown timer
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Schedule retry
    retryTimeoutRef.current = setTimeout(() => {
      clearInterval(countdownIntervalRef.current);
      executeRetry();
    }, delay);
  }, [autoRetry, retryCount, maxAutoRetries, getRetryDelay, executeRetry]);

  // Start auto-retry when modal opens or after failed retry
  useEffect(() => {
    if (isOpen && retryState === 'idle') {
      // Use timeout to avoid synchronous setState in effect
      const timeoutId = setTimeout(() => {
        scheduleAutoRetry();
      }, 0);
      return () => clearTimeout(timeoutId);
    } else if (retryState === 'failed' && retryCount < maxAutoRetries) {
      const timeoutId = setTimeout(() => {
        scheduleAutoRetry();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, retryState, retryCount, maxAutoRetries, scheduleAutoRetry]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Use timeout to avoid synchronous setState in effect
      const timeoutId = setTimeout(() => {
        setRetryState('idle');
        setRetryCount(0);
        setCountdown(0);
      }, 0);
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  // Handle manual retry
  const handleManualRetry = () => {
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    setCountdown(0);
    executeRetry();
  };

  // Handle dismiss
  const handleDismiss = () => {
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDismiss}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700 text-slate-100">
        {/* NewAudia Logo Header */}
        <div className="flex justify-center pt-2 pb-4">
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia" 
            className="h-12 w-auto rounded-lg"
          />
        </div>

        {/* Icon and Title */}
        <DialogHeader className="text-center">
          <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-3 ${
            retryState === 'success' 
              ? 'bg-emerald-500/10 border border-emerald-500/20' 
              : retryState === 'retrying'
                ? 'bg-amber-500/10 border border-amber-500/20'
                : 'bg-red-500/10 border border-red-500/20'
          }`}>
            {retryState === 'success' ? (
              <CheckCircle className="w-7 h-7 text-emerald-500" />
            ) : retryState === 'retrying' ? (
              <Loader2 className="w-7 h-7 text-amber-500 animate-spin" />
            ) : (
              <WifiOff className="w-7 h-7 text-red-500" />
            )}
          </div>
          <DialogTitle className="text-lg font-semibold text-white text-center">
            {retryState === 'success' ? 'Connected!' : retryState === 'retrying' ? 'Reconnecting...' : title}
          </DialogTitle>
        </DialogHeader>

        {/* Message Body */}
        <DialogDescription className="text-center text-slate-300 text-sm leading-relaxed px-4 py-2">
          {retryState === 'success' ? (
            'Connection restored. Continuing...'
          ) : retryState === 'retrying' ? (
            `Attempting to reconnect (attempt ${retryCount + 1})...`
          ) : (
            <>
              {message}
              {countdown > 0 && autoRetry && retryCount < maxAutoRetries && (
                <span className="block mt-2 text-amber-400">
                  Auto-retrying in {countdown}s...
                </span>
              )}
              {retryCount >= maxAutoRetries && (
                <span className="block mt-2 text-red-400">
                  Auto-retry limit reached. Please try manually or check your connection.
                </span>
              )}
            </>
          )}
        </DialogDescription>

        {/* Buttons */}
        {retryState !== 'success' && (
          <DialogFooter className="sm:justify-center pt-4 gap-3 flex-col sm:flex-row">
            <Button
              onClick={handleManualRetry}
              disabled={retryState === 'retrying'}
              className="w-full sm:w-auto min-w-[140px] bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50"
            >
              {retryState === 'retrying' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Now
                </>
              )}
            </Button>
            <Button
              onClick={handleDismiss}
              variant="outline"
              className="w-full sm:w-auto min-w-[100px] border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Dismiss
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NetworkErrorModal;
